/**
 * Normalizadores de valores compartidos por todos los importadores.
 *
 * Los archivos vienen de sistemas externos y de configuraciones regionales distintas: aqui
 * viven las conversiones que no dependen del modelo que se este importando.
 */

export const clean = (raw: unknown): string => `${raw ?? ""}`.trim();

/** Quita tildes y baja a minusculas para comparar valores de texto contra catalogos. */
export const simplify = (raw: unknown): string =>
    clean(raw)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const TRUE_VALUES = new Set(["si", "s", "true", "1", "x", "verdadero", "activo", "yes", "y"]);

/**
 * Valor booleano tolerante con lo que exportan los sistemas de facturacion.
 *
 * `si / s / yes / y / true / 1 / x / verdadero / activo` son verdadero; cualquier otra cosa
 * (incluidos el vacio, `no`, `0` y `false`) es falso.
 */
export const parseImportBoolean = (raw: unknown): boolean => TRUE_VALUES.has(simplify(raw));

/**
 * Convierte a entero tolerando los formatos regionales de los sistemas de facturacion:
 * "500000", "500,000", "500.000" y "₡500 000" tienen que dar 500000.
 */
export const parseImportInteger = (raw: unknown): number | undefined => {
    const value = clean(raw);
    if (!value) return undefined;

    const digits = value.replace(/[^\d-]/g, "");
    if (!digits || digits === "-") return undefined;

    const parsed = Number.parseInt(digits, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Convierte a decimal resolviendo cual separador es el decimal y cual el de miles.
 *
 * Reglas, pensadas para archivos exportados (no escritos a mano):
 * - Si aparecen los dos separadores, el ultimo es el decimal: "1.234,56" y "1,234.56" dan 1234.56.
 * - Si un separador aparece varias veces, es de miles: "1.234.567" da 1234567.
 * - Si aparece una sola vez: el punto siempre es decimal ("10.5" -> 10.5), y la coma es
 *   decimal salvo que la sigan exactamente tres digitos ("1,500" -> 1500, "1,50" -> 1.5),
 *   que es como escriben los miles los exportadores en espanol.
 *
 * Simbolos de moneda, porcentajes y espacios se descartan: "₡ 11 893" -> 11893, "13 %" -> 13.
 */
export const parseImportDecimal = (raw: unknown): number | undefined => {
    const value = clean(raw).replace(/[^\d.,-]/g, "");
    if (!value || !/\d/.test(value)) return undefined;

    const negative = value.startsWith("-");
    let body = value.replace(/-/g, "");

    const dots = (body.match(/\./g) || []).length;
    const commas = (body.match(/,/g) || []).length;

    if (dots && commas) {
        const decimalSeparator = body.lastIndexOf(".") > body.lastIndexOf(",") ? "." : ",";
        const thousandsSeparator = decimalSeparator === "." ? "," : ".";
        body = body.split(thousandsSeparator).join("");
        body = body.replace(decimalSeparator, ".");
    } else if (commas > 1) {
        body = body.split(",").join("");
    } else if (dots > 1) {
        body = body.split(".").join("");
    } else if (commas === 1) {
        const [, fraction] = body.split(",");
        body = fraction.length === 3 ? body.replace(",", "") : body.replace(",", ".");
    }

    const parsed = Number.parseFloat(body);
    if (Number.isNaN(parsed)) return undefined;

    return negative ? -parsed : parsed;
};

const CASH_CREDIT_TYPE_CREDIT = new Set(["credito", "credit", "1"]);
const CASH_CREDIT_TYPE_CASH = new Set(["contado", "cash", "0"]);

/**
 * Condicion de pago de un cliente o un proveedor: "Contado" -> 0, "Credito" -> 1.
 *
 * Los dos catalogos (`CLIENT_TYPES` y `PROVIDER_TYPES` en `utils/Constants`) usan los
 * mismos codigos, asi que el parser vive aqui en vez de duplicarse por modelo.
 * Devuelve `undefined` si el valor no se reconoce.
 */
export const parseCashCreditType = (raw: unknown): number | undefined => {
    const value = simplify(raw);
    if (!value) return undefined;
    if (CASH_CREDIT_TYPE_CREDIT.has(value)) return 1;
    if (CASH_CREDIT_TYPE_CASH.has(value)) return 0;
    return undefined;
};
